/*
 * ET - Reviews Carousel (sections/et-reviews-carousel.liquid)
 * <et-reviews-carousel> wraps prev arrow · .swiper · next arrow. Swiper comes from window.etLoadSwiper() (et-base-head).
 * Cards per view: 1 below 750px, up to 2 from 750px, up to 3 from 990px, data-cards-desktop from 1200px
 * (mirrored in assets/et-reviews-carousel.css for the pre-init layout).
 * Arrows and the optional dots / progress bar (Pagination setting, rendered right after the element inside
 * .et-reviews-carousel__inner) are only wired when the section rendered them.
 * The element creates one Swiper when connected and destroys it when disconnected, so a theme editor re-render
 * (shopify:section:load / unload) never leaves a duplicate instance. Blocks selected in the editor slide into view.
 * Autoplay (off by default) pauses on hover, stops for good once a visitor swipes, uses the arrows or moves keyboard
 * focus into the carousel, and is off for prefers-reduced-motion.
 * When Swiper cannot load, the cards stay in the CSS scroll-snap row and the arrows scroll that row instead.
 * Reviews source "Trustpilot (live)" (data-source="trustpilot"): before Swiper starts, the review block slides are
 * replaced by the latest Trustpilot reviews from window.etTrustpilot (assets/et-trustpilot.js), one clone of the
 * <template data-et-rc-tp-card> card per review. The blocks stay when the feed fails or returns nothing; a slider
 * without blocks is rendered hidden and only shown once live reviews arrive.
 * Live reviewer names are capitalised (data-tp-capitalize-names) and, when the full name does not fit its card,
 * shortened to the first name and the last name's initial (data-tp-short-names), re-measured whenever the carousel
 * resizes and once the web fonts have loaded.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-reviews-carousel')) return;

  const SLIDE_SELECTOR = '[data-et-rc-slide]';

  const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Name particles stay lower case inside a name: "jan van der berg" -> "Jan van der Berg" */
  const NAME_PARTICLES = new Set(['van', 'von', 'der', 'den', 'de', 'het', "'t", 'ter', 'ten', 'te', 'op', 'in', 'du', 'da', 'di', 'la', 'le']);

  /* Upper case first letter for every word and each hyphenated part; a word typed in capitals is lowered first */
  const capitalizeName = (name) => {
    const words = name.split(' ');
    return words
      .map((word, index) => {
        const lower = word.toLocaleLowerCase();
        if (index > 0 && index < words.length - 1 && NAME_PARTICLES.has(lower)) return lower;
        return word
          .split('-')
          .map((part) => {
            const chars = Array.from(part.length > 1 && part === part.toLocaleUpperCase() ? part.toLocaleLowerCase() : part);
            return chars.length ? chars[0].toLocaleUpperCase() + chars.slice(1).join('') : part;
          })
          .join('-');
      })
      .join(' ');
  };

  /* "Frank Schellekens" -> "Frank S."; empty when there is nothing to shorten */
  const shortName = (name) => {
    const words = name.split(' ');
    if (words.length < 2) return '';
    const initial = Array.from(words[words.length - 1]).find((char) => /\p{L}/u.test(char));
    const short = initial ? `${words[0]} ${initial.toLocaleUpperCase()}.` : '';
    return short === name ? '' : short;
  };

  class EtReviewsCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.loading = false;
      this.scrollArrows = null;
      this.visitorStopped = false;
      this.handleFocusIn = this.handleFocusIn.bind(this);
    }

    connectedCallback() {
      this.addEventListener('focusin', this.handleFocusIn);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.handleFocusIn);
      if (this.nameObserver) this.nameObserver.disconnect();
      this.nameObserver = null;
      this.destroy();
    }

    /* Keyboard or screen reader focus inside the carousel stops autoplay (WCAG 2.2.2) */
    handleFocusIn() {
      this.visitorStopped = true;
      this.pauseAutoplay();
    }

    get options() {
      const perViewDesktop = Math.max(1, parseInt(this.dataset.cardsDesktop, 10) || 4);
      const slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      const delay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
      const reducedMotion = prefersReducedMotion();
      const prevEl = this.querySelector('[data-et-rc-prev]');
      const nextEl = this.querySelector('[data-et-rc-next]');
      const scope = this.closest('.et-reviews-carousel__inner') || this;
      const dotsEl = scope.querySelector('[data-et-rc-dots]');
      const progressEl = scope.querySelector('[data-et-rc-progress]');

      return {
        slidesPerView: 1,
        spaceBetween: 16,
        speed: 400,
        watchOverflow: true,
        grabCursor: true,
        loop: this.dataset.loop === 'true' && slideCount > perViewDesktop,
        autoplay:
          this.dataset.autoplay === 'true' && !reducedMotion
            ? { delay, disableOnInteraction: true, pauseOnMouseEnter: true }
            : false,
        navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
        pagination: dotsEl ? { el: dotsEl, clickable: true } : false,
        scrollbar: progressEl ? { el: progressEl, draggable: true } : false,
        a11y: {
          enabled: true,
          prevSlideMessage: prevEl ? prevEl.getAttribute('aria-label') : undefined,
          nextSlideMessage: nextEl ? nextEl.getAttribute('aria-label') : undefined
        },
        on: {
          init: (swiper) => {
            this.syncLocked(swiper);
            this.fitNames();
          },
          resize: () => this.fitNames(),
          lock: (swiper) => this.syncLocked(swiper),
          unlock: (swiper) => this.syncLocked(swiper)
        },
        breakpoints: {
          750: { slidesPerView: Math.min(2, perViewDesktop) },
          990: { slidesPerView: Math.min(3, perViewDesktop) },
          1200: { slidesPerView: perViewDesktop }
        }
      };
    }

    init() {
      const swiperEl = this.querySelector('[data-et-rc-swiper]');
      if (!swiperEl || this.swiper || this.loading || !this.isConnected) return;
      if (this.dataset.source === 'trustpilot' && !this.liveLoaded) {
        this.loadLiveReviews();
        return;
      }
      this.watchNames();
      if (typeof window.etLoadSwiper !== 'function') {
        this.enableScrollArrows();
        return;
      }

      this.loading = true;
      window.etLoadSwiper().then((Swiper) => {
        this.loading = false;
        if (!this.isConnected || this.swiper) return;
        if (!Swiper) {
          this.enableScrollArrows();
          return;
        }
        this.disableScrollArrows();
        this.swiper = new Swiper(swiperEl, this.options);
        if (this.visitorStopped) this.pauseAutoplay();
      });
    }

    loadLiveReviews() {
      const api = window.etTrustpilot;
      const finish = () => {
        this.loading = false;
        this.liveLoaded = true;
        this.init();
      };
      if (!api || typeof api.reviews !== 'function') {
        finish();
        return;
      }

      this.loading = true;
      api
        .reviews({ stars: this.dataset.tpStars, languages: this.dataset.tpLanguages, count: this.dataset.tpCount })
        .then((reviews) => this.renderLiveReviews(reviews, api))
        .catch(() => {
          // Keep the review blocks.
        })
        .then(finish);
    }

    renderLiveReviews(reviews, api) {
      const template = this.querySelector('template[data-et-rc-tp-card]');
      const track = this.querySelector('[data-et-rc-track]');
      if (!template || !template.content.firstElementChild || !track || !reviews.length) return;

      const slides = reviews.map((review, index) => this.buildLiveSlide(template, review, index, api));
      track.replaceChildren(...slides);
      this.dataset.slideCount = String(slides.length);
      this.hidden = false;
    }

    buildLiveSlide(template, review, index, api) {
      const slide = template.content.firstElementChild.cloneNode(true);
      const field = (name) => slide.querySelector(`[data-et-rc-field="${name}"]`);
      const fill = (name, value) => {
        const element = field(name);
        if (!element) return;
        if (value) element.textContent = value;
        else element.remove();
      };

      slide.dataset.etRcIndex = String(index);
      fill('title', review.title);

      const name = review.name.replace(/\s+/g, ' ').trim();
      const displayName = this.dataset.tpCapitalizeNames === 'false' ? name : capitalizeName(name);
      fill('name', displayName);
      const nameElement = field('name');
      const short = nameElement && this.dataset.tpShortNames !== 'false' ? shortName(displayName) : '';
      if (short) {
        nameElement.dataset.etRcFullName = displayName;
        nameElement.dataset.etRcShortName = short;
      }

      fill('date', review.date ? api.formatDate(review.date) : '');

      /* Plain text from Trustpilot: blank lines become paragraphs, single line breaks a space (the card clamps lines) */
      const text = field('text');
      if (text) {
        const paragraphs = review.text
          .split(/\n\s*\n/)
          .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
          .filter(Boolean);
        if (paragraphs.length) {
          text.replaceChildren(
            ...paragraphs.map((paragraph) => {
              const element = document.createElement('p');
              element.textContent = paragraph;
              return element;
            })
          );
        } else {
          text.remove();
        }
      }

      const rating = field('rating');
      if (rating) {
        api.drawBoxes(slide.querySelector('svg.et-cart-page-rating-boxes'), review.stars);
        rating.textContent = (rating.dataset.etRcRatingText || '').replace('[rating]', String(review.stars));
      }

      slide
        .querySelectorAll('.et-reviews-carousel__card-content, .et-reviews-carousel__card-author, .et-reviews-carousel__card-footer')
        .forEach((wrapper) => {
          if (!wrapper.children.length) wrapper.remove();
        });

      return slide;
    }

    /* A name that does not fit its card shows the short form; always measured on the full name */
    fitNames() {
      this.querySelectorAll('[data-et-rc-short-name]').forEach((name) => {
        const { etRcFullName: full, etRcShortName: short } = name.dataset;
        if (name.textContent !== full) name.textContent = full;
        if (name.scrollWidth > name.clientWidth) name.textContent = short;
      });
    }

    watchNames() {
      if (this.nameObserver || !this.querySelector('[data-et-rc-short-name]')) return;
      const fit = () => {
        cancelAnimationFrame(this.nameFrame);
        this.nameFrame = requestAnimationFrame(() => this.fitNames());
      };
      if (typeof ResizeObserver === 'function') {
        this.nameObserver = new ResizeObserver(fit);
        this.nameObserver.observe(this);
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
      fit();
    }

    destroy() {
      this.disableScrollArrows();
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove('et-reviews-carousel__slider--locked');
    }

    /* No Swiper: the arrows scroll the scroll-snap row by one card and are disabled at either end */
    enableScrollArrows() {
      if (this.scrollArrows) return;
      const track = this.querySelector('[data-et-rc-track]');
      const prevEl = this.querySelector('[data-et-rc-prev]');
      const nextEl = this.querySelector('[data-et-rc-next]');
      if (!track || !prevEl || !nextEl) return;

      const update = () => {
        prevEl.disabled = track.scrollLeft <= 1;
        nextEl.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      };
      const scrollByCard = (direction) => {
        const slide = track.querySelector(SLIDE_SELECTOR);
        const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
        const distance = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
        track.scrollBy({ left: direction * distance, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      };
      const onPrev = () => scrollByCard(-1);
      const onNext = () => scrollByCard(1);

      prevEl.addEventListener('click', onPrev);
      nextEl.addEventListener('click', onNext);
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      this.scrollArrows = { track, prevEl, nextEl, onPrev, onNext, update };
      update();
    }

    disableScrollArrows() {
      if (!this.scrollArrows) return;
      const { track, prevEl, nextEl, onPrev, onNext, update } = this.scrollArrows;
      prevEl.removeEventListener('click', onPrev);
      nextEl.removeEventListener('click', onNext);
      track.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      this.scrollArrows = null;
    }

    pauseAutoplay() {
      if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
    }

    /* All reviews fit (the arrows are hidden by .swiper-button-lock): centre the cards. */
    syncLocked(swiper) {
      this.classList.toggle('et-reviews-carousel__slider--locked', Boolean(swiper.isLocked));
    }

    selectSlide(slide) {
      if (!this.swiper) return;
      const index = parseInt(slide.dataset.etRcIndex, 10);
      if (Number.isNaN(index)) return;

      this.pauseAutoplay();
      if (this.swiper.params.loop) this.swiper.slideToLoop(index, 0);
      else this.swiper.slideTo(index, 0);
    }

    resumeAutoplay() {
      if (this.visitorStopped) return;
      if (this.swiper && this.swiper.params.autoplay && this.swiper.params.autoplay.enabled && this.swiper.autoplay) {
        this.swiper.autoplay.start();
      }
    }
  }

  window.customElements.define('et-reviews-carousel', EtReviewsCarousel);

  const carouselsIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-reviews-carousel')) : [];

  const slideFrom = (event) => (event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null);

  document.addEventListener('shopify:section:load', (event) => carouselsIn(event.target).forEach((el) => el.init()));
  document.addEventListener('shopify:section:unload', (event) => carouselsIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = slideFrom(event);
    const carousel = slide && slide.closest('et-reviews-carousel');
    if (carousel) carousel.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slide = slideFrom(event);
    const carousel = slide && slide.closest('et-reviews-carousel');
    if (carousel) carousel.resumeAutoplay();
  });
})();
